import React, { memo, useState, useCallback } from 'react';
import { Handle, Position, useConnection } from '@xyflow/react';
import classes from '../../FlowNodes.module.scss';
import { ReactComponent as PlusIcon } from 'new/components/icons/fi-sr-plus-small.svg';
import { ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import { Z_INDEX } from 'js/components/pipelines/FlowNodes/constants';
import { useSplitterSupport } from 'new/hooks/useFeatureFlags';

const menuPaperStyles: React.CSSProperties = {
  minWidth: 120,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  borderRadius: 8,
  zIndex: Z_INDEX.FLOW_CANVAS_OVERLAY,
};

const menuTransformOrigin = {
  horizontal: 'left' as const,
  vertical: 'center' as const,
};

const menuAnchorOrigin = {
  horizontal: 'right' as const,
  vertical: 'center' as const,
};

interface TerminatingHandleProps {
  /** Handle ID for React Flow */
  id?: string;
  /** Callback when Transform is selected */
  onTransform?: () => void;
  /** Callback when Connector/Send is selected */
  onTarget?: () => void;
  /** Callback when Split is selected. Omit to hide the Split option. */
  onSplit?: () => void;
  /** Layout direction for positioning */
  layoutDirection?: 'LR' | 'TB';
}

/**
 * Terminating handle for API server nodes without children.
 * Displays a horizontal line with a pulsing + icon button.
 * Supports both drag-to-connect and click-to-open-menu interactions.
 */
export const TerminatingHandle = memo<TerminatingHandleProps>(
  ({ id = 'right', onTransform, onTarget, onSplit, layoutDirection = 'LR' }) => {
    const connectionInProgress = useConnection((connection) => connection.inProgress);
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const isSplitterEnabled = useSplitterSupport();

    const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setMenuAnchor(event.currentTarget);
    }, []);

    const handleCloseMenu = useCallback(() => {
      setMenuAnchor(null);
    }, []);

    const handleTransformClick = useCallback(() => {
      handleCloseMenu();
      onTransform?.();
    }, [onTransform, handleCloseMenu]);

    const handleTargetClick = useCallback(() => {
      handleCloseMenu();
      onTarget?.();
    }, [onTarget, handleCloseMenu]);

    const handleSplitClick = useCallback(() => {
      handleCloseMenu();
      onSplit?.();
    }, [onSplit, handleCloseMenu]);

    return (
      <>
        <Tooltip title="Drag or click to create a new node">
          <Handle
            type="source"
            position={layoutDirection === 'TB' ? Position.Bottom : Position.Right}
            id={id}
            className={classes.apiServerTerminatingHandle}
            isConnectable={!connectionInProgress}
            onClick={handleClick}
          >
            <div className={classes.apiServerHandleLine} />
            <div className={classes.apiServerHandlePulse}>
              <PlusIcon className={classes.apiServerPlusIcon} />
            </div>
          </Handle>
        </Tooltip>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleCloseMenu}
          transformOrigin={menuTransformOrigin}
          anchorOrigin={menuAnchorOrigin}
          slotProps={{
            paper: {
              style: menuPaperStyles,
            },
          }}
        >
          <MenuItem onClick={handleTransformClick}>
            <ListItemText>Transform</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleTargetClick}>
            <ListItemText>Connector</ListItemText>
          </MenuItem>
          {isSplitterEnabled && onSplit ? (
            <MenuItem onClick={handleSplitClick}>
              <ListItemText>Split</ListItemText>
            </MenuItem>
          ) : null}
        </Menu>
      </>
    );
  },
);

TerminatingHandle.displayName = 'TerminatingHandle';
